import gql from 'graphql-tag';

import { CatchFragments } from './fragments/CatchType';
import { CountryFragments } from './fragments/CountryType';
import { FishingWaterFragments } from './fragments/FishingWaterType';
import { ImageFragments } from './fragments/ImageType';
import { PlaceFragments } from './fragments/PlaceType';
import { PostFragments } from './fragments/PostType';
import { RegionFragments } from './fragments/RegionType';
import { SpeciesFragments } from './fragments/SpeciesTypes';
import { UserFragments } from './fragments/UserType';

export const getPlaceCoordinates = gql`
  query GetPlaceCoordinates($countryCode: String!, $regionCode: String!, $placeSlug: String!) {
    entry: place(countryCode: $countryCode, regionCode: $regionCode, slug: $placeSlug) {
      ...PlaceId
      latitude
      longitude
    }
  }
  ${PlaceFragments.id}
`;

export const getPlace = gql`
  query GetPlace($countryCode: String!, $regionCode: String!, $placeSlug: String!) {
    entry: place(countryCode: $countryCode, regionCode: $regionCode, slug: $placeSlug) {
      ...PlaceId
      name
      slug
      latitude
      longitude
      catches(first: 10, filters: { withPhotos: true, orderByCaughtAt: true }) {
        edges {
          node {
            ...CatchId
            post {
              ...PostId
              catch {
                ...CatchId
                ...CatchFishingWaterName
                ...CatchSpeciesName
                caughtAtGmt
                weight
                length
              }
              comments {
                totalCount
              }
              createdAt
              displayProductUnits {
                totalCount
              }
              ...PostDisplayEntities
              ...PostProductUnits
              ...PostVideoFields
              images(first: 10, croppingStrategy: SMART, height: 400, width: 400) {
                totalCount
                edges {
                  node {
                    ...ImageFields
                  }
                }
              }
              likedByCurrentUser
              likesCount
              text {
                text
              }
              user {
                ...UserId
                ...UserAvatar
                nickname
              }
            }
          }
        }
      }
      country {
        ...CountryId
        countryName
        topFishingWaters(first: 20) {
          edges {
            node {
              _id: externalId
              displayName
            }
          }
        }
        topSpecies(first: 20) {
          edges {
            node {
              _id: externalId
              displayName
            }
          }
        }
      }
      region {
        ...RegionId
        regionCode
        name
        licenseInfoUrl
      }
    }
  }
  ${CatchFragments.id}
  ${CatchFragments.fishingWaterName}
  ${CatchFragments.speciesName}
  ${PostFragments.id}
  ${PostFragments.productUnits}
  ${PostFragments.displayEntities}
  ${PostFragments.videoFields}
  ${ImageFragments.imageFields}
  ${CountryFragments.id}
  ${PlaceFragments.id}
  ${RegionFragments.id}
  ${UserFragments.avatar}
  ${UserFragments.id}
`;

export const getPlaceTopAnglers = gql`
  query GetPlaceTopAnglers(
    $countryCode: String!
    $regionCode: String!
    $placeSlug: String!
    $catchesDateRange: DateRangeInputObject!
  ) {
    entry: place(countryCode: $countryCode, regionCode: $regionCode, slug: $placeSlug) {
      ...PlaceId
      topAnglers: catches(filters: { caughtBetweenDates: $catchesDateRange }) {
        anglers: topAnglers(first: 3) {
          totalCount
          edges {
            node {
              ...UserId
              followedByCurrentUser
              displayIcon(height: 256, width: 256) {
                width: width
                height: height
                url
              }
              displayName
              nickname
            }
          }
        }
      }
    }
  }
  ${PlaceFragments.id}
  ${UserFragments.id}
`;

export const getPlaceNearbyPlaces = gql`
  query GetPlaceNearbyPlaces($countryCode: String!, $regionCode: String!, $placeSlug: String!) {
    entry: place(countryCode: $countryCode, regionCode: $regionCode, slug: $placeSlug) {
      ...PlaceId
      nearbyPlaces(first: 24) {
        edges {
          distanceUnit
          node {
            ...PlaceId
            name
            slug
            latitude
            longitude
            country {
              ...CountryId
            }
            region {
              ...RegionId
            }
          }
        }
      }
    }
  }
  ${PlaceFragments.id}
  ${CountryFragments.id}
  ${RegionFragments.id}
`;

export const getPlaceTopWaters = gql`
  query GetPlaceTopWaters($first: Int, $boundingBox: BoundingBoxInputObject) {
    entry: fishingWaters(
      first: $first
      filters: { orderByCatchesCount: true, publicOnly: true, withCatches: true }
      boundingBox: $boundingBox
    ) {
      edges {
        node {
          ...FishingWaterId
          ...FishingWaterLocationFields
          catchesCount
          displayName
          allReviews: reviews {
            totalCount
            totalAverage(attribute: RATING)
          }
          recentCatches: catches(first: 4, filters: { withPhotos: true, orderByRecent: true }) {
            edges {
              node {
                ...CatchId
                post {
                  ...PostId
                  images(first: 1, croppingStrategy: SMART, height: 400, width: 400) {
                    totalCount
                    edges {
                      node {
                        ...ImageFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${CatchFragments.id}
  ${PostFragments.id}
  ${ImageFragments.imageFields}
  ${FishingWaterFragments.id}
  ${FishingWaterFragments.locationFields}
`;

export const getPlaceWatersByTopSpecies = gql`
  query GetPlaceWatersByTopSpecies($boundingBox: BoundingBoxInputObject, $speciesIDs: [String!]) {
    entry: fishingWaters(
      first: 1
      filters: { publicOnly: true, withCatchesForSpeciesIds: $speciesIDs }
      boundingBox: $boundingBox
    ) {
      totalCount
    }
  }
`;

export const getPlaceTopSpecies = gql`
  query GetPlaceTopSpecies($countryCode: String!, $regionCode: String!, $placeSlug: String!) {
    entry: place(countryCode: $countryCode, regionCode: $regionCode, slug: $placeSlug) {
      ...PlaceId
      topSpeciesSummary(first: 15) {
        edges {
          catchesCount
          node {
            ...SpeciesId
            displayName
            image {
              ...ImageFields
            }
          }
        }
      }
    }
  }
  ${PlaceFragments.id}
  ${ImageFragments.imageFields}
  ${SpeciesFragments.id}
`;
